import { _ } from "../../polyglot/polyglot.js";
import {
	checkIcon,
	infoCircleIcon,
	timesIcon,
} from "../../elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import AlertMessageWidget from "../AlertMessageWidget/AlertMessageWidget.js";
import FeatureSet from "../../../models/packaging/FeatureSet.js";
import ModalWidget from "../ModalWidget.js";

/**
 * Display a table comparing Packages specificities. Only features found in at least one
 * Package will be rendered.
 * This widget prevents the user from picking a "smaller" package.
 *
 * @param {Package[]} packages - A list of packages to be compared one with
 *   another
 * @param {string} [selectionId] - The id of the Package that should be
 *   rendered selected
 * @param {string} [cssClass] - An optional css class for the table. Defaults
 *   to "packages-table"
 * @param {boolean} [canUserForcePackageSelection] - Allowed users can force
 *   the package selection, even to a "smaller" package. This has a huge impact
 *   on invoicing, so we should be careful when passing this option
 * @param {function} [onSelection] - a function to perform specific actions
 *   when a package is selected. Takes a Package id and a callback function as
 *   parameters. Selection is disabled if this function is falsy
 */
export default class PackagesTableWidget extends Widget {
	constructor({
		packages,
		selectionId,
		cssClass = "packages-table",
		canUserForcePackageSelection = false,
		onSelection,
	}) {
		super(...arguments);

		this._packages = packages;
		this._cssClass = cssClass;

		const features = packages.flatMap((pkg) => pkg.getFeatures());
		this._featureSet = new FeatureSet({ features });
		this._canUserForcePackageSelection = canUserForcePackageSelection;
		this._onSelection = onSelection;

		this._selectedPackage = null;
		this._packageIdToTierLevel = {}; // we use a map for O(1) access
		this._setSelectedPackage({ packages, selectionId });
		this._cacheTierLevels(packages);
	}

	renderContentOn(html) {
		html.div(
			{ class: ["PackagesTableWidget", this._cssClass] },
			this._renderWarningOn.bind(this),
			html.div(
				{ class: "table-content" },
				html.div(
					{ class: "features column" },
					this._renderFeatureNamesOn.bind(this),
				),
				this._renderPackagesOn.bind(this),
			),
		);
	}

	_renderWarningOn(html) {
		if (this._canUserForcePackageSelection) return;

		html.render(
			new AlertMessageWidget({
				content: _(
					"To reduce your package size, please contact your administrator.",
				),
				dismissible: false,
				dataTestName: "warning",
				type: "warning",
			}),
		);
	}

	_renderFeatureNamesOn(html) {
		// the heading of the features column seems obvious enough to not write "Features" here:
		html.div({ class: "head cell" });

		[...this._featureSet].forEach((feature) => {
			const descriptionModal = new ModalWidget({
				cssClass: "dark-modal",
				title: feature.getName(),
				content: feature.getDescription(),
				severity: "info",
			});

			html.div(
				{
					class: "cell feature-name",
					click: () => descriptionModal.show(),
				},
				feature.getName(),
				infoCircleIcon,
			);
		});
	}

	_renderPackagesOn(html) {
		this._packages.forEach((pkg) =>
			html.div(
				{
					class: [
						"package",
						"column",
						{
							selected: pkg === this._selectedPackage,
							selectable: this._canBeClicked({ package: pkg }),
						},
					],
					click: this._clickPackageHeader.bind(this, pkg),
				},
				this._packageNameRenderer(pkg),
				this._packageFeaturesRenderer(pkg),
			),
		);
	}

	_packageNameRenderer(pkg) {
		return (html) =>
			html.div(
				{
					class: "head cell",
				},
				this._bannerRenderer(pkg),
				pkg.getName(),
			);
	}

	_clickPackageHeader(pkg) {
		if (!this._canBeClicked({ package: pkg })) {
			return;
		}

		this._onSelection({
			pkg,
			callback: this._updateSelectedPackage.bind(this, pkg),
		});
	}

	_canBeClicked({ package: pkg }) {
		if (!this._onSelection) return false;

		if (this._canUserForcePackageSelection) return true;

		return (
			this._packageIdToTierLevel[pkg.getId()] >=
			this._packageIdToTierLevel[this._selectedPackage.getId()]
		);
	}

	_setSelectedPackage({ packages, selectionId }) {
		this._selectedPackage = packages.find(
			(each) => each.getId() === selectionId,
		);
	}

	_cacheTierLevels(packages) {
		this._packageIdToTierLevel = packages.reduce((result, pkg) => {
			result[pkg.getId()] = pkg.getLevel();
			return result;
		}, {});
	}

	_bannerRenderer(pkg) {
		return (html) => {
			if (pkg !== this._selectedPackage) {
				return;
			}

			html.div({ class: "banner" }, checkIcon);
		};
	}

	_updateSelectedPackage(pkg) {
		this._selectedPackage = pkg;
		this.update();
	}

	_packageFeaturesRenderer(pkg) {
		return (html) =>
			[...this._featureSet].forEach((feature) =>
				html.div(
					{
						class: "cell",
						"data-test": `${pkg.getId()}_${feature.getId()}`,
					},
					pkg.hasFeature(feature) ? checkIcon : timesIcon,
				),
			);
	}
}
